import { pageSizeMap } from "@reactive-resume/utils";
import { toBlob } from "html-to-image";
import { PDFDocument } from "pdf-lib";

const MM_TO_PT = 72 / 25.4;

type Progress = {
  current: number;
  total: number;
};

export type GeneratePdfOptions = {
  frame: HTMLIFrameElement | null;
  format: keyof typeof pageSizeMap;
  title?: string;
  pixelRatio?: number;
  onProgress?: (progress: Progress) => void;
};

const waitForFonts = async (doc: Document) => {
  try {
    if ("fonts" in doc && typeof doc.fonts?.ready?.then === "function") {
      await doc.fonts.ready;
    }
  } catch {
    // Ignore failures; rendering will fall back to available fonts
  }
};

const waitForImages = async (doc: Document) => {
  const images = Array.from(doc.images);

  await Promise.all(
    images.map((image) => {
      if (image.complete && image.naturalHeight !== 0) return Promise.resolve();

      return new Promise<void>((resolve) => {
        const cleanup = () => {
          image.removeEventListener("load", onLoad);
          image.removeEventListener("error", onError);
        };

        const onLoad = () => {
          cleanup();
          resolve();
        };

        const onError = () => {
          cleanup();
          resolve();
        };

        image.addEventListener("load", onLoad, { once: true });
        image.addEventListener("error", onError, { once: true });
      });
    }),
  );
};

const dataPageSelector = '[data-page]';

const mmToPoints = (value: number) => value * MM_TO_PT;

export const generatePdfFromArtboard = async ({
  frame,
  format,
  title,
  pixelRatio = 2,
  onProgress,
}: GeneratePdfOptions): Promise<Blob> => {
  if (!frame) {
    throw new Error("Artboard is not ready yet. Please try again in a moment.");
  }

  const doc = frame.contentDocument ?? frame.contentWindow?.document;
  if (!doc) {
    throw new Error("Unable to access artboard content. Please refresh the page and try again.");
  }

  await waitForFonts(doc);
  await waitForImages(doc);

  const pages = Array.from(doc.querySelectorAll<HTMLElement>(dataPageSelector)).sort((a, b) => {
    const pageA = Number.parseInt(a.dataset.page ?? "0", 10);
    const pageB = Number.parseInt(b.dataset.page ?? "0", 10);
    return pageA - pageB;
  });

  if (pages.length === 0) {
    throw new Error("No resume pages were found. Please ensure your resume has content.");
  }

  const { width: widthMm, height: heightMm } = pageSizeMap[format] ?? pageSizeMap.a4;
  const pageWidthPt = mmToPoints(widthMm);
  const pageHeightPt = mmToPoints(heightMm);

  const pdfDoc = await PDFDocument.create();

  for (const [index, pageNode] of pages.entries()) {
    onProgress?.({ current: index, total: pages.length });

    const computedStyle = doc.defaultView ? doc.defaultView.getComputedStyle(pageNode) : null;
    const backgroundColor = computedStyle?.backgroundColor ?? "#ffffff";

    const blob = await toBlob(pageNode, {
      pixelRatio,
      cacheBust: true,
      backgroundColor,
      // Ensure html-to-image doesn't inherit zoom/pan transforms
      style: {
        transform: "none",
      },
    });

    if (!blob) {
      throw new Error("Failed to capture one of the resume pages. Please try again.");
    }

    const arrayBuffer = await blob.arrayBuffer();
    const image = await pdfDoc.embedPng(arrayBuffer);
    const scaled = image.scaleToFit(pageWidthPt, pageHeightPt);

    const pdfPage = pdfDoc.addPage([pageWidthPt, pageHeightPt]);
    pdfPage.drawImage(image, {
      ...scaled,
      x: (pageWidthPt - scaled.width) / 2,
      y: (pageHeightPt - scaled.height) / 2,
    });
  }

  onProgress?.({ current: pages.length, total: pages.length });

  const pdfBytes = await pdfDoc.save();

  return new Blob([pdfBytes], { type: "application/pdf", endings: "native" });
};

